import React from 'react';
import { KeyboardAvoidingView, useWindowDimensions, VirtualizedList, View, Pressable, StyleSheet } from 'react-native';
import { ThemeConsumer } from "@rneui/themed";

import CommonHeader from '../../shared/commonComponents/commonHeader';

import { IconType, ZenDivider, ZenIcon, ZenText } from 'zen-core-elements';
import { useAppNavigation } from '../../types/@react-navigation/navigation';


type listType = {
    name: string;
    navigate: string;
    content: string;
    iconName: string;
    iconType?: IconType;
    iconBgColor: string;
    iconColor: string;
};

function SettingScreen(): React.JSX.Element {
    const { width } = useWindowDimensions();
    const navigation = useAppNavigation();

    // Variable to hold the data of the store settings list with suitable icons & theme colors
    const list: listType[] = [
        {
            name: 'Store Details',
            navigate: 'StoreSetting',
            content: 'Configure Store profile, Store status, customize store logo and favorite icon, provide SEO and Social media details.',
            iconName: 'storefront-outline',
            iconType: 'material-community',
            iconBgColor: '#E8F0FE',
            iconColor: '#1A73E8',
        },
        {
            name: 'Locations',
            navigate: 'LocationStack',
            content: 'Managing Multiple Store Locations, Update Store Location Information, Shop Working Time, Order Placing Time, Handle location availability.',
            iconName: 'map-marker-outline',
            iconType: 'material-community',
            iconBgColor: '#E6F4EA',
            iconColor: '#1E8E3E',
        },
        {
            name: 'Zone Management',
            navigate: 'ZoneStack',
            content: 'Zones will be used for setting up and managing tax and shipping rates for different postal codes, cities, states and countries more effectively.',
            iconName: 'map-search-outline',
            iconType: 'material-community',
            iconBgColor: '#FEF7E0',
            iconColor: '#B06000',
        },
        {
            name: 'Display',
            navigate: 'DisplaySettings',
            content: 'Manage Order Handling fees, hold amount, tip, Additional charge, order expiration days, view return actions and reasons.',
            iconName: 'monitor-dashboard',
            iconType: 'material-community',
            iconBgColor: '#FCE8E6',
            iconColor: '#D93025',
        },
        {
            name: 'Orders',
            navigate: 'OrderSettings',
            content: 'Configure order management preferences, order fulfillment, manual order settings, and cart options.',
            iconName: 'clipboard-list-outline',
            iconType: 'material-community',
            iconBgColor: '#EEF2FF',
            iconColor: '#4F46E5',
        },
        {
            name: 'Products',
            navigate: 'ProductSettings',
            content: 'Configure automatic SKU generation (prefix, separator, category / brand / variant codes, sequence) and choose which fields print on the product label.',
            iconName: 'barcode-scan',
            iconType: 'material-community',
            iconBgColor: '#F3E8FF',
            iconColor: '#7C3AED',
        },
        {
            name: 'Receipts',
            navigate: 'ReceiptsSettings',
            content: 'Customize the appearance of your order and gift receipts by adding location info, customer info, logo, header, footer or notes.',
            iconName: 'receipt-outline',
            iconType: 'ionicon',
            iconBgColor: '#E0F2F1',
            iconColor: '#00796B',
        },
    ];

    const renderItem = ({ item, index }: { item: listType[], index: number }) => {
        const numOfColumns = width > 600 ? 2 : 1;
        return (
            <ThemeConsumer>
                {({ theme }) => (
                    <View key={index} style={{ flexDirection: "row" }}>
                        {item?.map((setting: listType, ResponseDataIndex: number) => (
                            <View key={ResponseDataIndex} style={{ flex: 1 }}>
                                <Pressable
                                    style={({ pressed }) => [
                                        styles.card,
                                        {
                                            backgroundColor: theme?.light || '#FFFFFF',
                                            borderColor: theme?.lightGrey || '#E2E8F0',
                                            opacity: pressed ? 0.85 : 1,
                                        }
                                    ]}
                                    onPress={() => navigation.navigate(setting?.navigate as any)}
                                >
                                    <View style={styles.cardHeader}>
                                        <View
                                            style={[
                                                styles.iconContainer,
                                                { backgroundColor: setting?.iconBgColor }
                                            ]}
                                        >
                                            <ZenIcon
                                                name={setting?.iconName}
                                                type={setting?.iconType || 'material-community'}
                                                size={22}
                                                color={setting?.iconColor}
                                            />
                                        </View>

                                        <View style={{ flex: 1 }}>
                                            <ZenText
                                                content={setting?.name}
                                                size={theme?.smallFontSize}
                                                style={{ fontFamily: theme?.bold, color: theme?.primary }}
                                            />
                                        </View>

                                        <ZenIcon
                                            name="chevron-right"
                                            type="material-community"
                                            size={20}
                                            color={theme?.grey || '#94A3B8'}
                                        />
                                    </View>

                                    <ZenDivider style={{ height: 0.6, backgroundColor: theme?.lightGrey || '#F1F5F9' }} />

                                    <ZenText
                                        content={setting?.content}
                                        size={theme?.smallFontSize}
                                        style={{
                                            fontFamily: theme?.medium,
                                            color: theme?.grey || '#64748B',
                                            lineHeight: 18,
                                        }}
                                    />
                                </Pressable>
                            </View>
                        ))}
                        {numOfColumns > 1 && item?.length < numOfColumns && (
                            <View style={{ flex: 1 }} />
                        )}
                    </View>
                )}
            </ThemeConsumer>
        );
    };

    const getItem = (item: any[], index: number) => {
        let array: any = [];
        let numOfColumns = width > 600 ? 2 : 1;
        for (let i = 0; i < numOfColumns; i++) {
            let temp = item?.[index * numOfColumns + i];
            temp && array?.push(temp);
        }
        return array;
    };

    const numOfColumns = width > 600 ? 2 : 1;

    return (
        <ThemeConsumer>
            {({ theme }: any) => (
                <KeyboardAvoidingView style={{ flex: 1, backgroundColor: theme.lightBackgroundColor || '#F8FAFC' }}>
                    <CommonHeader headerName='Pos Setting' />
                    <View style={{ flex: 1, paddingHorizontal: 6, paddingTop: 10 }}>
                        <VirtualizedList
                            keyExtractor={(_: any, index: number) => index?.toString()}
                            getItemCount={(data: any) => Math.ceil((data?.length || 0) / numOfColumns)}
                            getItem={getItem}
                            scrollEnabled={true}
                            nestedScrollEnabled={true}
                            renderItem={renderItem}
                            data={list}
                            keyboardDismissMode="on-drag"
                            showsVerticalScrollIndicator={false}
                            contentContainerStyle={[{ paddingBottom: 20 }]}
                        />
                    </View>
                </KeyboardAvoidingView>
            )}
        </ThemeConsumer>
    );
}

const styles = StyleSheet.create({
    card: {
        marginBottom: 12,
        marginHorizontal: 8,
        padding: 14,
        borderRadius: 10,
        gap: 12,
        borderWidth: 1,
        elevation: 1,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.04,
        shadowRadius: 2,
    },
    cardHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    iconContainer: {
        width: 42,
        height: 42,
        borderRadius: 10,
        alignItems: 'center',
        justifyContent: 'center',
    },
});

export default SettingScreen;

